import {
  getDiscountInfo,
  isCreditMemoSelectable,
  QuickbooksCreditMemo,
  QuickbooksInvoice,
  SupplierIntegrationType,
} from '@dill/dill-shared';

interface DistributedInvoice {
  invoice: QuickbooksInvoice;
  amountToPay: number;
  discountAmount: number;
  isPartialPay: boolean;
}

interface DistributionResult {
  distributedInvoices: DistributedInvoice[];
  /** Credit memos to send with the payment. Each one is applied in full. */
  appliedCredits: QuickbooksCreditMemo[];
  totalCreditsUsed: number;
  /** Cash left over once every open invoice is cleared — goes out as an open payment. */
  excessAmount: number;
  totalInvoiceAmount: number;
  totalDiscount: number;
}

const round2 = (value: number) => Number(value.toFixed(2));

const balanceOf = (invoice: QuickbooksInvoice) => invoice.balance ?? invoice.amount ?? 0;

/**
 * The "invoice date" shown in the invoice tables, for invoices and credit memos alike.
 * Due date is the fallback so a document without one still lands in a stable spot.
 */
const invoiceDateOf = (document: { createdAt?: Date | null; dueDate?: Date | null }) => {
  const date = document.createdAt ?? document.dueDate;
  return date ? new Date(date).getTime() : Infinity;
};

const byInvoiceDate = (
  a: { createdAt?: Date | null; dueDate?: Date | null },
  b: { createdAt?: Date | null; dueDate?: Date | null }
) => {
  const dateDiff = invoiceDateOf(a) - invoiceDateOf(b);
  if (dateDiff !== 0) return dateDiff;
  const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
  const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
  return aDue - bDue;
};

/**
 * Spreads a fixed payment over a buyer's open documents, oldest invoice date first.
 *
 * Invoices and credit memos sit on one timeline: a credit is free coverage on top of the cash
 * amount, so the oldest credits are pulled in before the cash runs out and the payment clears
 * more invoices than the cash alone would. Without them a buyer who pays their statement net of
 * credits short-pays the last invoice.
 */
export function distributeFixedAmountToInvoices({
  fixedAmount,
  openInvoices,
  openCreditMemos = [],
  integrationType,
  domainId,
  discountEligible,
}: {
  fixedAmount: number;
  openInvoices: QuickbooksInvoice[];
  openCreditMemos?: QuickbooksCreditMemo[];
  integrationType?: SupplierIntegrationType | null;
  domainId?: string | null;
  discountEligible?: boolean;
}): DistributionResult {
  const payableInvoices = [...openInvoices]
    .filter((invoice) => balanceOf(invoice) > 0)
    .sort(byInvoiceDate);

  if (!payableInvoices.length || fixedAmount <= 0) {
    return {
      distributedInvoices: [],
      appliedCredits: [],
      totalCreditsUsed: 0,
      excessAmount: fixedAmount,
      totalInvoiceAmount: 0,
      totalDiscount: 0,
    };
  }

  // If payment method is not eligible for discounts, skip discount calculation
  const shouldCalculateDiscount = discountEligible !== false;
  const discountFor = (invoice: QuickbooksInvoice, amount: number) =>
    shouldCalculateDiscount
      ? getDiscountInfo(
          { editedPayment: amount, invoiceOriginal: invoice },
          integrationType,
          domainId
        ).discountAmount
      : 0;

  // What it costs to clear every open invoice, and so also the most credit this payment can
  // absorb: the server applies credits against these same amounts and stops there.
  const totalPayoffCost = round2(
    payableInvoices.reduce((sum, invoice) => {
      const balance = balanceOf(invoice);
      return sum + balance - discountFor(invoice, balance);
    }, 0)
  );

  const eligibleCredits = openCreditMemos
    .filter(
      (credit) =>
        (credit.remainingCredit ?? 0) > 0 &&
        credit.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
        // Same gate the server uses to decide whether it will apply credits at all. Sending a
        // credit it ignores would bill the buyer for the credited amount in cash.
        isCreditMemoSelectable(credit.recordType, integrationType)
    )
    .sort(byInvoiceDate);

  // Oldest credits first, and only as far as the cash falls short. A credit is taken whole or
  // left alone: a partly used one would let the server apply more credit than this split budgets
  // for, and the buyer's bank would be debited less than the amount they entered.
  const appliedCredits: QuickbooksCreditMemo[] = [];
  let totalCreditsUsed = 0;
  for (const credit of eligibleCredits) {
    if (round2(fixedAmount + totalCreditsUsed) >= totalPayoffCost) break;
    const creditAmount = credit.remainingCredit ?? 0;
    if (round2(totalCreditsUsed + creditAmount) > totalPayoffCost) continue;
    appliedCredits.push(credit);
    totalCreditsUsed = round2(totalCreditsUsed + creditAmount);
  }

  const distributedInvoices: DistributedInvoice[] = [];
  let remaining = round2(fixedAmount + totalCreditsUsed);
  let totalInvoiceAmount = 0;
  let totalDiscount = 0;

  for (const invoice of payableInvoices) {
    if (remaining <= 0) break;

    const balance = balanceOf(invoice);
    const discountAmount = discountFor(invoice, balance);
    const amountNeeded = balance - discountAmount;

    if (remaining >= amountNeeded) {
      // Pay this invoice in full
      distributedInvoices.push({
        invoice,
        amountToPay: balance,
        discountAmount,
        isPartialPay: false,
      });
      remaining -= amountNeeded;
      totalInvoiceAmount += balance;
      totalDiscount += discountAmount;
    } else {
      // Partial pay: remaining covers part of the balance
      // Recalculate discount for the partial amount
      const partialDiscount = discountFor(invoice, remaining);

      distributedInvoices.push({
        invoice,
        amountToPay: remaining,
        discountAmount: partialDiscount,
        isPartialPay: true,
      });
      totalInvoiceAmount += remaining;
      totalDiscount += partialDiscount;
      remaining = 0;
    }
  }

  return {
    distributedInvoices,
    appliedCredits,
    totalCreditsUsed,
    // Credits are only taken as far as the invoices can absorb them, so whatever is left over
    // here is unspent cash.
    excessAmount: round2(remaining),
    totalInvoiceAmount: round2(totalInvoiceAmount),
    totalDiscount: round2(totalDiscount),
  };
}
